import { useQuery } from '@tanstack/react-query';
import environment from '@config/environment';
import TokenManager from '@viome/viome-base/src/api/TokenManager';
import { useUserStore } from '@hooks/useUserStore';

interface PeacockAcls {
   admin:   boolean;
   peacock: boolean;
}

const ACL_TIMEOUT_MS = 10000;

// Hits /api.php?fctn=acl-my-status on the Peacock host and returns the parsed
// ACL flags. Shares the React-Query cache with anything else keyed by
// ['peacock-acl', email], so multiple consumers (sidebar, bottom nav, future
// gates) trigger only one network call per email change.
export const usePeacockAcl = (): PeacockAcls & { isLoading: boolean } => {
   const { user } = useUserStore ();
   const email = user?.email;

   const { data, isLoading } = useQuery<PeacockAcls> ({
      queryKey: ['peacock-acl', email],
      enabled:  !!email,
      staleTime: 0,
      retry:     false,
      queryFn: async () => {
         const controller = new AbortController ();
         const timeoutId  = window.setTimeout (
            () => controller.abort (),
            ACL_TIMEOUT_MS,
         );
         try {
            const token = TokenManager.getToken ();
            const res   = await fetch (
               `${environment.PEACOCK_API_BASE}/api.php?fctn=acl-my-status`,
               {
                  credentials: 'include',
                  signal:      controller.signal,
                  headers:     token ? { 'X-Viome-Auth': token } : undefined,
               },
            );
            if (!res.ok) return { admin: false, peacock: false };
            const json = await res.json ();
            return {
               admin:   json?.acls?.admin   === true,
               peacock: json?.acls?.peacock === true,
            };
         } catch {
            return { admin: false, peacock: false };
         } finally {
            window.clearTimeout (timeoutId);
         }
      },
   });

   return {
      admin:    data?.admin   ?? false,
      peacock:  data?.peacock ?? false,
      isLoading,
   };
};
